/**
 * TanStack Query hooks for shift data management
 *
 * This provides the single source of truth for all shift data in the application.
 * Uses stale-while-revalidate pattern for optimal UX - data is always visible
 * while fresh data loads in the background.
 *
 * IMPORTANT: All date range queries should use business timezone for boundaries.
 * When businessTimezone is provided, dates are interpreted as business-local times
 * and converted to UTC for querying.
 */

import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { startOfMonth, endOfMonth, startOfDay, endOfDay } from 'date-fns';
import { UnifiedShiftService, UnifiedShiftQueryResult } from '@/lib/scheduling/unified-shift-service';
import { DayShift, Employee } from '@/types';
import { getBusinessDayQueryBounds } from '@/lib/core/timezone-utils';

// Query key factory for consistent cache keys
export const shiftQueryKeys = {
  all: ['shifts'] as const,
  lists: () => [...shiftQueryKeys.all, 'list'] as const,
  list: (businessId: string, startDate: string, endDate: string) =>
    [...shiftQueryKeys.lists(), businessId, startDate, endDate] as const,
  day: (businessId: string, date: string) =>
    [...shiftQueryKeys.all, 'day', businessId, date] as const,
};

export interface UseShiftsQueryOptions {
  businessId: string | null;
  startDate: Date;
  endDate: Date;
  employees?: Employee[];
  hidePastDays?: boolean;
  enabled?: boolean;
}

export interface UseShiftsQueryResult {
  shifts: DayShift[];
  isLoading: boolean;
  isFetching: boolean;
  isRefetching: boolean;
  error: Error | null;
  refetch: () => void;
  performanceStats: UnifiedShiftQueryResult['performanceStats'] | null;
}

/**
 * Main hook for fetching shifts for a date range
 *
 * Features:
 * - Automatic caching with 5 minute stale time
 * - Keeps previous data visible while fetching new data
 * - Background refetching without loading states
 * - Shared cache across all components using same query
 */
export function useShiftsQuery({
  businessId,
  startDate,
  endDate,
  employees = [],
  hidePastDays = false,
  enabled = true,
}: UseShiftsQueryOptions): UseShiftsQueryResult {
  const query = useQuery({
    queryKey: shiftQueryKeys.list(
      businessId || '',
      startDate.toISOString(),
      endDate.toISOString()
    ),
    queryFn: async (): Promise<UnifiedShiftQueryResult> => {
      if (!businessId) {
        return {
          shifts: [],
          totalCount: 0,
          historicalCount: 0,
          currentCount: 0,
          performanceStats: {
            storedStatusCount: 0,
            realTimeCalculatedCount: 0,
            cacheHitRate: 0,
          },
        };
      }

      return UnifiedShiftService.fetchShiftsForDateRange(
        businessId,
        startDate,
        endDate,
        employees,
        hidePastDays
      );
    },
    enabled: enabled && !!businessId,
    // Keep data fresh for 5 minutes before background refetch
    staleTime: 1000 * 60 * 5,
    // Keep unused data in cache for 30 minutes
    gcTime: 1000 * 60 * 30,
    // Show previous data while fetching new data (eliminates loading states)
    placeholderData: keepPreviousData,
    // Don't refetch on window focus - this causes jarring UX when switching tabs
    refetchOnWindowFocus: false,
    // Don't refetch on mount if data is fresh
    refetchOnMount: false,
  });

  return {
    shifts: query.data?.shifts || [],
    isLoading: query.isLoading, // Only true on initial load with no cached data
    isFetching: query.isFetching, // True whenever fetching (including background)
    isRefetching: query.isRefetching, // True only for background refetches
    error: query.error,
    refetch: query.refetch,
    performanceStats: query.data?.performanceStats || null,
  };
}

/**
 * Hook for fetching shifts for a specific month
 * Convenience wrapper around useShiftsQuery
 */
export function useMonthShiftsQuery({
  businessId,
  month,
  employees = [],
  hidePastDays = false,
  enabled = true,
}: {
  businessId: string | null;
  month: Date;
  employees?: Employee[];
  hidePastDays?: boolean;
  enabled?: boolean;
}): UseShiftsQueryResult {
  return useShiftsQuery({
    businessId,
    startDate: startOfMonth(month),
    endDate: endOfMonth(month),
    employees,
    hidePastDays,
    enabled,
  });
}

/**
 * Hook for fetching shifts for a single day
 *
 * When businessTimezone is provided, the date boundaries are calculated
 * in the business timezone, not the browser timezone. This ensures that
 * shifts are queried for the correct business day.
 *
 * @param businessTimezone - IANA timezone string (e.g., "Europe/Bratislava")
 */
export function useDayShiftsQuery({
  businessId,
  date,
  employees = [],
  enabled = true,
  businessTimezone,
}: {
  businessId: string | null;
  date: Date;
  employees?: Employee[];
  enabled?: boolean;
  businessTimezone?: string;
}): UseShiftsQueryResult {
  // If business timezone is provided, use timezone-aware boundaries
  // Otherwise, fall back to browser timezone (for backwards compatibility)
  let startDate: Date;
  let endDate: Date;

  if (businessTimezone) {
    const bounds = getBusinessDayQueryBounds(date, businessTimezone);
    startDate = bounds.startUtc;
    endDate = bounds.endUtc;
  } else {
    // Legacy behavior - uses browser timezone
    startDate = startOfDay(date);
    endDate = endOfDay(date);
  }

  return useShiftsQuery({
    businessId,
    startDate,
    endDate,
    employees,
    enabled,
  });
}

/**
 * Hook for fetching today's shifts
 *
 * @param businessTimezone - IANA timezone string for accurate "today" calculation
 */
export function useTodayShiftsQuery({
  businessId,
  employees = [],
  enabled = true,
  businessTimezone,
}: {
  businessId: string | null;
  employees?: Employee[];
  enabled?: boolean;
  businessTimezone?: string;
}): UseShiftsQueryResult {
  const today = new Date();
  return useDayShiftsQuery({
    businessId,
    date: today,
    employees,
    enabled,
    businessTimezone,
  });
}

/**
 * Hook to invalidate shifts cache
 * Use this after mutations to trigger background refetch
 */
export function useInvalidateShifts() {
  const queryClient = useQueryClient();

  return {
    // Invalidate all shifts for a business
    invalidateAll: (businessId: string) => {
      queryClient.invalidateQueries({
        queryKey: [...shiftQueryKeys.all],
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && key.includes(businessId);
        },
      });
    },
    // Invalidate specific date range
    invalidateRange: (businessId: string, startDate: Date, endDate: Date) => {
      queryClient.invalidateQueries({
        queryKey: shiftQueryKeys.list(
          businessId,
          startDate.toISOString(),
          endDate.toISOString()
        ),
      });
    },
    // Invalidate all shifts queries (nuclear option)
    invalidateAllShifts: () => {
      queryClient.invalidateQueries({
        queryKey: shiftQueryKeys.all,
      });
    },
  };
}

/**
 * Hook to prefetch shifts for adjacent months
 * Call this to preload data for better UX when navigating
 */
export function usePrefetchShifts() {
  const queryClient = useQueryClient();

  return {
    prefetchMonth: async (
      businessId: string,
      month: Date,
      employees: Employee[] = []
    ) => {
      const startDate = startOfMonth(month);
      const endDate = endOfMonth(month);

      await queryClient.prefetchQuery({
        queryKey: shiftQueryKeys.list(
          businessId,
          startDate.toISOString(),
          endDate.toISOString()
        ),
        queryFn: () =>
          UnifiedShiftService.fetchShiftsForDateRange(
            businessId,
            startDate,
            endDate,
            employees,
            false
          ),
        staleTime: 1000 * 60 * 5,
      });
    },
  };
}
